import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { Input } from "src/components/ui/input";
import BorderedSelect from "src/components/form-new/bordered-select";
import type { InternalReferral, ReferralTier } from "../types/commissions.types";

const TIER_OPTIONS: { value: ReferralTier; label: string }[] = [
  { value: "existing", label: "Existing (6%)" },
  { value: "new", label: "New (20%)" },
  { value: "after_one_year", label: "After 1 yr (10%)" },
];

interface InternalReferralDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referral: InternalReferral | null;
  isNew: boolean;
  onSave: (referral: Partial<InternalReferral> & { id?: string }) => void;
  employeeOptions?: { value: string; label: string }[];
  clientOptions?: { value: string; label: string }[];
}

const InternalReferralDrawer: React.FC<InternalReferralDrawerProps> = ({
  open,
  onOpenChange,
  referral,
  isNew,
  onSave,
  employeeOptions = [],
  clientOptions = [],
}) => {
  const [referringEmployeeId, setReferringEmployeeId] = useState("");
  const [clientId, setClientId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [tier, setTier] = useState<ReferralTier>("new");

  useEffect(() => {
    if (open) {
      if (referral) {
        setReferringEmployeeId(referral.referringEmployeeId);
        setClientId(referral.clientId);
        setStartDate(referral.startDate);
        setTier(referral.tier);
      } else if (isNew) {
        setReferringEmployeeId("");
        setClientId("");
        setStartDate("");
        setTier("new");
      }
    }
  }, [open, referral, isNew]);

  const handleSave = () => {
    const employeeName = employeeOptions.find((o) => o.value === referringEmployeeId)?.label ?? "";
    const clientName = clientOptions.find((o) => o.value === clientId)?.label ?? "";
    onSave({
      ...(referral?.id ? { id: referral.id } : {}),
      referringEmployeeId,
      referringEmployeeName: employeeName,
      clientId,
      clientName,
      startDate,
      tier,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!w-1/2 !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className={portalDs.sheetTitle}>
            {isNew ? "Add internal referral" : "Edit internal referral"}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Referring employee</label>
            {employeeOptions.length > 0 ? (
              <BorderedSelect
                label=""
                options={[{ value: "", label: "Select employee" }, ...employeeOptions]}
                value={referringEmployeeId}
                onChange={(e) => setReferringEmployeeId(e.target.value)}
              />
            ) : (
              <Input
                value={referringEmployeeId}
                onChange={(e) => setReferringEmployeeId(e.target.value)}
                placeholder="Employee name or ID"
                className={portalDs.input}
              />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Client</label>
            {clientOptions.length > 0 ? (
              <BorderedSelect
                label=""
                options={[{ value: "", label: "Select client" }, ...clientOptions]}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            ) : (
              <Input
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Client name or ID"
                className={portalDs.input}
              />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Start date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={`${portalDs.input} max-w-xs`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={portalDs.label}>Referral tier</label>
            <BorderedSelect
              label=""
              options={TIER_OPTIONS}
              value={tier}
              onChange={(e) => setTier(e.target.value as ReferralTier)}
            />
            <p className={portalDs.hint}>
              Customer status at referral: Existing 6%, New 20%. After 1 year from the start date, rate is 10%.
            </p>
          </div>
        </div>
        <SheetFooter>
          <PortalDsButton type="button" variant="primary" size="sm" className="px-6" onClick={handleSave}>
            Save
          </PortalDsButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default InternalReferralDrawer;
